import { CnapsDashboardAlert } from "../components/CnapsPanel";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "../lib/api";
import { useRealtime } from "../context/RealtimeContext";
import { useAuth } from "../context/AuthContext";
import { RevenueTrendCard, RevenueByClientCard, RevenueBySiteCard } from "../components/RevenueCharts";
import { CoveragePreviewCard } from "../components/CoveragePreviewCard";
import {
  Users, Building2, CalendarClock, FileClock, Clock, AlertTriangle,
  ShieldAlert, CheckCheck, MapPin, Wallet, TrendingUp, TrendingDown, ChevronLeft, ChevronRight, Receipt, BadgeEuro, FileDown
} from "lucide-react";
import { MonthInput } from "../components/DateInput";

const shiftMonth = (ym, d) => { const [y, m] = ym.split("-").map(Number); const t = new Date(y, m - 1 + d, 1); return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}`; };
const longMonth = (ym) => new Date(`${ym}-01T12:00:00`).toLocaleDateString("fr-FR", { month: "long", year: "numeric" });

function Delta({ cur, prev }) {
  if (!prev && !cur) return <span className="text-xs text-slate-400">—</span>;
  if (!prev) return <span className="text-xs font-semibold text-emerald-700">Nouveau</span>;
  const pct = Math.round(((cur - prev) / prev) * 100);
  const up = pct >= 0;
  const Icon = up ? TrendingUp : TrendingDown;
  return <span className={`inline-flex items-center gap-1 text-xs font-bold rounded-full px-2 py-0.5 ${up ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}><Icon className="h-3 w-3" />{up ? "+" : ""}{pct}%</span>;
}

function RevenueCard({ rev }) {
  if (!rev) return null;
  const { current: c, previous: p } = rev;
  const tile = (label, Icon, cur, prev, sub, prevSub, testid) => (
    <div className="flex-1 p-5" data-testid={testid}>
      <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500"><Icon className="h-4 w-4 text-slate-400" />{label}</div>
      <div className="flex items-baseline gap-3 mt-2">
        <div className="font-display text-3xl font-extrabold tracking-tight">{fmtEUR(cur)}</div>
        <Delta cur={cur} prev={prev} />
      </div>
      <div className="text-xs text-slate-500 mt-1">{sub}</div>
      <div className="text-xs text-slate-400 mt-0.5">{longMonth(p.month)} : {fmtEUR(prev)} {prevSub}</div>
    </div>
  );
  return (
    <div className="border border-slate-200 bg-white rounded-md mb-8" data-testid="revenue-card">
      <div className="px-5 pt-4 flex items-center justify-between">
        <h3 className="font-display text-xl font-bold flex items-center gap-2"><BadgeEuro className="h-5 w-5 text-emerald-600" /> Chiffre d'affaires — <span className="capitalize">{longMonth(c.month)}</span></h3>
        <span className="text-[11px] text-slate-400">Visible uniquement par l'administrateur · comparé à {longMonth(p.month)}</span>
      </div>
      <div className="flex flex-col md:flex-row md:divide-x divide-slate-100">
        {tile("Facturé TTC", Receipt, c.invoiced_ttc, p.invoiced_ttc, `${c.invoiced_count} facture(s) émise(s) · ${fmtEUR(c.invoiced_ht)} HT`, `(${p.invoiced_count} fact.)`, "revenue-invoiced")}
        {tile("Encaissé TTC", Wallet, c.paid_ttc, p.paid_ttc, `${c.paid_count} facture(s) réglée(s) ce mois`, `(${p.paid_count} fact.)`, "revenue-paid")}
      </div>
    </div>
  );
}

function ExportButton({ month }) {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      const r = await http.get(`/stats/export.pdf?month=${month}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      Object.assign(document.createElement("a"), { href: url, download: `Tableau_de_bord_${month}.pdf` }).click();
    } finally { setBusy(false); }
  };
  return (
    <button data-testid="dashboard-export-pdf" onClick={go} disabled={busy} className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-md bg-slate-900 hover:bg-slate-800 text-white disabled:opacity-60">
      <FileDown className="h-4 w-4" /> {busy ? "Génération…" : "Exporter en PDF"}
    </button>
  );
}

function MonthPicker({ value, onChange }) {
  const nowYm = new Date().toISOString().slice(0, 7);
  return (
    <div className="flex items-center gap-1 border border-slate-300 rounded-md bg-white" data-testid="dashboard-month-picker">
      <button data-testid="month-prev" onClick={() => onChange(shiftMonth(value, -1))} className="p-2 hover:bg-slate-100 rounded-l-md" title="Mois précédent"><ChevronLeft className="h-4 w-4" /></button>
      <MonthInput data-testid="month-input" value={value} onChange={(e) => e.target.value && onChange(e.target.value)} className="text-sm font-semibold px-1 py-1.5 outline-none bg-transparent" />
      <button data-testid="month-next" onClick={() => onChange(shiftMonth(value, 1))} className="p-2 hover:bg-slate-100" title="Mois suivant"><ChevronRight className="h-4 w-4" /></button>
      {value !== nowYm && <button data-testid="month-today" onClick={() => onChange(nowYm)} className="text-xs font-semibold px-2.5 py-2 border-l border-slate-200 hover:bg-slate-100 rounded-r-md">Mois en cours</button>}
    </div>
  );
}

const fmtEUR = (n) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);
const monthLabel = (ym) => new Date(`${ym}-01T12:00:00`).toLocaleDateString("fr-FR", { month: "short", year: "2-digit" });

function CashflowCard() {
  const [cf, setCf] = useState(null);
  const { lastEvent } = useRealtime();
  const load = () => http.get("/stats/cashflow").then((r) => setCf(r.data)).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (lastEvent?.event === "facture_changed") load(); }, [lastEvent]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!cf) return null;
  const bars = [
    { key: "overdue", label: "En retard", amount: cf.overdue.amount, count: cf.overdue.count, cls: "bg-rose-500" },
    ...cf.months.map((m, i) => ({ key: m.month, label: monthLabel(m.month), amount: m.amount, count: m.count, cls: i === 0 ? "bg-emerald-600" : "bg-emerald-400" })),
    ...(cf.later.count ? [{ key: "later", label: "Au-delà", amount: cf.later.amount, count: cf.later.count, cls: "bg-slate-400" }] : []),
  ];
  const max = Math.max(1, ...bars.map((b) => b.amount));
  return (
    <div className="border border-slate-200 bg-white rounded-md p-6 mb-8" data-testid="cashflow-card">
      <div className="flex items-start justify-between mb-5">
        <div>
          <h3 className="font-display text-xl font-bold flex items-center gap-2"><Wallet className="h-5 w-5 text-emerald-600" /> Prévisionnel de trésorerie</h3>
          <p className="text-xs text-slate-500">Montants TTC restant dus sur les factures non payées, par mois d'échéance · visible uniquement par l'administrateur</p>
        </div>
        <div className="text-right">
          <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Total attendu</div>
          <div className="font-display text-2xl font-extrabold" data-testid="cashflow-total">{fmtEUR(cf.total)}</div>
        </div>
      </div>
      <div className="flex items-end gap-3 h-44">
        {bars.map((b) => (
          <Link to="/factures" key={b.key} className="flex-1 flex flex-col items-center justify-end h-full group" data-testid={`cashflow-${b.key}`} title={`${b.count} facture(s)`}>
            <div className="text-[11px] font-mono font-semibold text-slate-700 mb-1">{b.amount ? fmtEUR(b.amount) : "—"}</div>
            <div className={`w-full max-w-[64px] rounded-t ${b.cls} group-hover:opacity-80 transition-opacity`} style={{ height: `${Math.max(b.amount ? 4 : 1, (b.amount / max) * 100)}%` }} />
            <div className={`text-[11px] mt-1.5 font-semibold capitalize ${b.key === "overdue" ? "text-rose-700" : "text-slate-600"}`}>{b.label}</div>
            <div className="text-[10px] text-slate-400">{b.count} fact.</div>
          </Link>
        ))}
      </div>
    </div>
  );
}

function Stat({ label, value, accent = "sky", icon: Icon, sub, to }) {
  const accents = {
    sky: "from-sky-500 to-sky-700",
    rose: "from-rose-500 to-rose-700",
    emerald: "from-emerald-500 to-emerald-700",
    indigo: "from-indigo-500 to-indigo-700",
    amber: "from-amber-400 to-amber-600",
    slate: "from-slate-700 to-slate-900",
  };
  const content = (
    <>
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">{label}</span>
        <div className={`h-8 w-8 rounded-md bg-gradient-to-br ${accents[accent]} grid place-items-center text-white`}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
      <div className="font-display text-4xl font-extrabold tracking-tight">{value}</div>
      {sub && <div className="text-xs text-slate-500">{sub}</div>}
    </>
  );
  const base = "border border-slate-200 bg-white rounded-md p-5 flex flex-col gap-3 transition-colors";
  if (to) {
    return (
      <Link
        to={to}
        data-testid={`stat-link-${to.replace(/[/?=]/g, "-")}`}
        className={`${base} hover:shadow-sm cursor-pointer`}
        style={{ transition: "border-color 150ms, box-shadow 150ms" }}
        onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#ab7f53"; }}
        onMouseLeave={(e) => { e.currentTarget.style.borderColor = ""; }}
      >
        {content}
      </Link>
    );
  }
  return <div className={`${base} hover:border-slate-300`}>{content}</div>;
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [alerts, setAlerts] = useState({ pending: [], expiring_soon: [], expired: [] });
  const [agents, setAgents] = useState([]);
  const [offSiteCheckins, setOffSiteCheckins] = useState([]);
  const { lastEvent } = useRealtime();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const hasFactures = !user?.modules_off?.includes("factures");
  const hasDocs = !user?.modules_off?.includes("documents");
  const [month, setMonth] = useState(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; });
  const monthName = longMonth(month);

  const load = useCallback(async () => {
    try {
      const today = new Date(); const start = new Date(today.getTime() - 24*3600*1000);
      const fmtLocal = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
      const startStr = fmtLocal(start);
      const endStr = fmtLocal(today);
      const results = await Promise.allSettled([
        http.get(`/stats?month=${month}`),
        http.get("/documents/alerts"),
        http.get("/agents"),
        http.get(`/check-ins?start=${startStr}&end=${endStr}`),
      ]);
      if (results[0].status === "fulfilled") setStats(results[0].value.data);
      if (results[1].status === "fulfilled") setAlerts(results[1].value.data);
      if (results[2].status === "fulfilled") setAgents(results[2].value.data);
      if (results[3].status === "fulfilled") setOffSiteCheckins((results[3].value.data || []).filter(x => x.off_site));
    } catch {
      // 401 redirect handled by axios interceptor
    }
  }, [month]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!lastEvent) return;
    if (["vacation_changed", "agent_changed", "site_changed", "document_changed", "company_changed", "facture_changed", "devis_changed", "checkin_changed", "offsite_alert"].includes(lastEvent.event)) {
      load();
    }
  }, [lastEvent, load]);

  const agentName = (id) => { const a = agents.find(x=>x.id===id); return a ? `${a.last_name} ${a.first_name}` : "—"; };

  return (
    <div className="p-8 max-w-[1800px] mx-auto" data-testid="dashboard">
      <div className="flex items-end justify-between mb-8">
        <div>
          <h1 className="font-display text-4xl font-extrabold tracking-tight">Tableau de bord</h1>
          <p className="text-sm text-slate-600 mt-1">Vue d&apos;ensemble en direct de votre exploitation.</p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && <ExportButton month={month} />}
          <MonthPicker value={month} onChange={setMonth} />
        </div>
      </div>

      <div className={`grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8 ${isAdmin ? "lg:grid-cols-5" : "lg:grid-cols-3"}`}>
        <Stat label="Agents" value={stats?.total_agents ?? 0} accent="sky" icon={Users} sub="Total enregistrés" to="/agents" />
        <Stat label="Sites actifs" value={stats?.active_sites_month ?? 0} accent="indigo" icon={Building2} sub={`Avec vacations en ${monthName} · ${stats?.total_sites ?? 0} au total`} to="/sites" />
        {isAdmin && <Stat label="Vacations du mois" value={stats?.month_vacations ?? 0} accent="slate" icon={CalendarClock} sub={`Planifiées en ${monthName}`} to="/planning" />}
        {isAdmin && <Stat label="Heures du mois" value={`${stats?.month_hours ?? 0}h`} accent="emerald" icon={Clock} sub={`Planifiées en ${monthName}`} to="/heures" />}
        <Stat label="Non affectées" value={stats?.unassigned ?? 0} accent="rose" icon={AlertTriangle} sub={`Couverture ${stats?.coverage_pct ?? 0}%`} to="/planning?filter=unassigned" />
      </div>

      <CnapsDashboardAlert />
      {hasDocs && <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8">
        <Stat label="Documents en attente" value={stats?.pending_docs ?? 0} accent="amber" icon={FileClock} sub="À valider" to="/documents?status=pending" />
        <Stat label="Documents refusés" value={stats?.rejected_docs ?? 0} accent="rose" icon={ShieldAlert} to="/documents?status=rejected" />
      </div>}

      {isAdmin && hasFactures && <RevenueCard rev={stats?.revenue} />}
      {isAdmin && hasFactures && (
        <div className="grid grid-cols-1 xl:grid-cols-5 gap-x-4">
          <div className="xl:col-span-3"><RevenueTrendCard month={month} /></div>
          <div className="xl:col-span-2"><RevenueByClientCard month={month} /><RevenueBySiteCard month={month} /></div>
        </div>
      )}
      {isAdmin && hasFactures && <CashflowCard />}
      <CoveragePreviewCard isAdmin={isAdmin} />

      {/* Répartition */}
      <div className="border border-slate-200 bg-white rounded-md p-6 mb-8">
        <h3 className="font-display text-xl font-bold mb-4">Répartition par type de vacation</h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            { k: "regular", l: "Standard", cls: "shift-regular" },
            { k: "night", l: "Nuit", cls: "shift-night" },
            { k: "holiday", l: "Férié", cls: "shift-holiday" },
            { k: "sunday", l: "Dimanche", cls: "shift-sunday" },
            { k: "unassigned", l: "Non affecté", cls: "shift-unassigned" },
          ].map((t) => (
            <Link
              key={t.k}
              to={`/planning?shift=${t.k}`}
              data-testid={`shift-${t.k}`}
              className={`${t.cls} rounded-md px-4 py-3 text-center hover:opacity-90 hover:shadow-sm transition-all cursor-pointer`}
            >
              <div className="text-xs uppercase font-bold tracking-widest opacity-80">{t.l}</div>
              <div className="font-display text-2xl font-extrabold mt-1">{stats?.by_type?.[t.k] ?? 0}</div>
            </Link>
          ))}
        </div>
      </div>

      {/* Pending docs */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {hasDocs && <div className="border border-slate-200 bg-white rounded-md p-6" data-testid="pending-docs-panel">
          <div className="flex items-center justify-between mb-4">
            <Link to="/documents?status=pending" className="font-display text-xl font-bold hover:text-sky-700 transition-colors">Documents en attente</Link>
            <CheckCheck className="h-5 w-5 text-slate-400" />
          </div>
          {alerts.pending.length === 0 ? (
            <div className="text-sm text-slate-500 py-6 text-center">Aucun document en attente.</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {alerts.pending.slice(0, 6).map((d) => (
                <Link
                  key={d.id}
                  to={`/documents?agent=${d.agent_id}&status=pending`}
                  className="flex items-center justify-between py-3 hover:bg-slate-50 rounded px-2 -mx-2 transition-colors"
                >
                  <div>
                    <div className="font-semibold text-sm">{d.label}</div>
                    <div className="text-xs text-slate-500">{agentName(d.agent_id)} · {d.doc_type}</div>
                  </div>
                  <span className="doc-pending rounded-md px-2 py-1 text-[10px] font-bold uppercase tracking-widest">En attente</span>
                </Link>
              ))}
            </div>
          )}
        </div>}

        <div className="border border-slate-200 bg-white rounded-md p-6" data-testid="expiring-docs">
          <div className="flex items-center justify-between mb-4">
            <Link to="/agents" className="font-display text-xl font-bold inline-flex items-center gap-2 hover:text-sky-700 transition-colors"><AlertTriangle className="h-5 w-5 text-amber-500"/>Documents bientôt expirés</Link>
            <span className="text-[10px] uppercase tracking-widest font-bold text-slate-500">&lt; 30 jours</span>
          </div>
          {alerts.expiring_soon.length === 0 && alerts.expired.length === 0 ? (
            <div className="text-sm text-slate-500 py-6 text-center">Aucune alerte.</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {alerts.expired.slice(0, 5).map((d) => (
                <Link
                  key={d.id}
                  to={`/agents?edit=${d.agent_id}`}
                  className="flex items-center justify-between py-3 hover:bg-slate-50 rounded px-2 -mx-2 transition-colors"
                >
                  <div>
                    <div className="font-semibold text-sm">{d.label}</div>
                    <div className="text-xs text-slate-500">{agentName(d.agent_id)} · expiré le {d.expires_on}</div>
                  </div>
                  <span className="doc-expired rounded-md px-2 py-1 text-[10px] font-bold uppercase tracking-widest">Expiré</span>
                </Link>
              ))}
              {alerts.expiring_soon.slice(0, 6).map((d) => (
                <Link
                  key={d.id}
                  to={`/agents?edit=${d.agent_id}`}
                  className="flex items-center justify-between py-3 hover:bg-slate-50 rounded px-2 -mx-2 transition-colors"
                >
                  <div>
                    <div className="font-semibold text-sm">{d.label}</div>
                    <div className="text-xs text-slate-500">{agentName(d.agent_id)} · expire le {d.expires_on}</div>
                  </div>
                  <span className={`${d._days_left <= 7 ? "doc-rejected" : "doc-pending"} rounded-md px-2 py-1 text-[10px] font-bold uppercase tracking-widest`}>
                    {d._days_left <= 0 ? "Aujourd'hui" : `J-${d._days_left}`}
                  </span>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>

      {offSiteCheckins.length > 0 && (
        <div className="mt-4 border border-rose-200 bg-rose-50 rounded-md p-5" data-testid="offsite-alerts">
          <div className="flex items-center gap-2 mb-3">
            <MapPin className="h-5 w-5 text-rose-600"/>
            <h3 className="font-display text-xl font-bold text-rose-900">Pointages hors zone (dernières 24h)</h3>
            <span className="ml-auto bg-rose-600 text-white text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded">{offSiteCheckins.length}</span>
          </div>
          <div className="divide-y divide-rose-200">
            {offSiteCheckins.map((c) => (
              <div key={c.id} className="py-2 flex items-center justify-between text-sm">
                <div>
                  <div className="font-semibold">{c.agent_name} · {c.kind === "in" ? "Arrivée" : "Départ"}{c.site_name ? ` à ${c.site_name}` : ""}</div>
                  <div className="text-xs text-rose-700">{new Date(c.at).toLocaleString("fr-FR", { day:"2-digit", month:"short", hour:"2-digit", minute:"2-digit" })} · à <b>{c.distance_m?.toFixed(0)} m</b> du site (rayon {c.radius_m} m)</div>
                </div>
                {c.lat && c.lon && <a href={`https://www.openstreetmap.org/?mlat=${c.lat}&mlon=${c.lon}#map=18/${c.lat}/${c.lon}`} target="_blank" rel="noreferrer" className="text-xs text-rose-700 hover:underline inline-flex items-center gap-1"><MapPin className="h-3 w-3"/>Voir</a>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
